/**
 * Step-completion detectors for the interactive help guide.
 *
 * See docs/INTERACTIVE_HELP_PLAN.md §6. Four kinds, plus an `any` combinator:
 *
 *   route       — the current pathname matches
 *   dom         — a selector gains (or loses, with `absent`) an on-screen match
 *   transaction — `palcare:transaction` fires with a matching transType
 *   manual      — the user clicks Next; always available as a fallback
 *
 * `subscribe` returns an unsubscribe function and calls `onDone()` at most once.
 * Manual advance is handled by the engine, not here — it needs no subscription.
 */

import { isOnScreen } from './useAnchor.js';

export const TRANSACTION_EVENT = 'palcare:transaction';

/**
 * Does any *on-screen* element match this selector? (§5.3 visibility rule.)
 *
 * Exported because every "is this UI actually present" question in the engine
 * must use this and not a bare querySelector: several drawers in this codebase
 * are rendered unconditionally and merely parked off-screen when closed
 * (ResidentsPage.jsx:1453 mounts ResidentDrawer permanently), so existence in the
 * DOM says nothing about whether the user can see it.
 */
export function selectorVisible(selector) {
    if (!selector) return false;
    let nodes;
    try {
        nodes = document.querySelectorAll(selector);
    } catch {
        return false; // malformed selector in a flow file — fail closed
    }
    for (const el of nodes) {
        if (isOnScreen(el)) return true;
    }
    return false;
}

/** True when `detector` is satisfied purely by inspecting the DOM right now. */
export function isSatisfiedNow(detector, { pathname } = {}) {
    if (!detector) return false;
    if (detector.any) return detector.any.some((d) => isSatisfiedNow(d, { pathname }));
    switch (detector.type) {
        case 'route':
            return matchesRoute(detector, pathname);
        case 'dom':
            return detector.absent ? !selectorVisible(detector.selector) : selectorVisible(detector.selector);
        default:
            // transaction/manual are events — never already-satisfied.
            return false;
    }
}

function matchesRoute(detector, pathname) {
    if (!pathname) return false;
    if (detector.pattern) {
        try {
            return new RegExp(detector.pattern).test(pathname);
        } catch {
            return false;
        }
    }
    if (detector.path) return pathname.startsWith(detector.path);
    return false;
}

/**
 * Watch for completion.
 *
 * @param {object} detector — from the flow's step `done`
 * @param {object} ctx — { getPathname: () => string }
 * @param {() => void} onDone
 * @returns {() => void} unsubscribe
 */
export function subscribe(detector, ctx, onDone) {
    if (!detector) return () => {};

    let done = false;
    const fire = () => {
        if (done) return;
        done = true;
        onDone();
    };

    if (detector.any) {
        const unsubs = detector.any.map((d) => subscribe(d, ctx, fire));
        return () => unsubs.forEach((u) => u());
    }

    switch (detector.type) {
        case 'transaction': {
            const wanted = new Set(detector.transTypes || []);
            const handler = (e) => {
                if (wanted.has(e?.detail?.transType)) fire();
            };
            window.addEventListener(TRANSACTION_EVENT, handler);
            return () => window.removeEventListener(TRANSACTION_EVENT, handler);
        }

        case 'dom': {
            // Poll + observe. The observer catches subtree changes; the interval
            // is the backstop for transform/class-only flips (a drawer sliding in
            // mutates nothing in the DOM tree, only its computed transform).
            const check = () => {
                const hit = detector.absent
                    ? !selectorVisible(detector.selector)
                    : selectorVisible(detector.selector);
                if (hit) fire();
            };
            const observer = new MutationObserver(check);
            /* `disabled` is in the filter so a selector like
               `[data-help-id="…"]:not([disabled])` reacts the moment a form
               becomes valid — that's how form validation drives step completion
               (see moveInResident step 2). `class`/`style` cover drawers sliding
               in, which mutate no nodes, only their transform. */
            observer.observe(document.body, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'style', 'disabled'],
            });
            const poll = setInterval(check, 250);
            check();
            return () => {
                observer.disconnect();
                clearInterval(poll);
            };
        }

        case 'route': {
            // The engine re-subscribes on navigation, so an immediate check is
            // all that's needed here.
            const poll = setInterval(() => {
                if (matchesRoute(detector, ctx.getPathname())) fire();
            }, 200);
            if (matchesRoute(detector, ctx.getPathname())) fire();
            return () => clearInterval(poll);
        }

        case 'manual':
        case 'manualAdvanceOnNext':
        default:
            // Engine-driven: the callout renders a Next button.
            return () => {};
    }
}

/** Should the callout show an explicit Next button for this detector? */
export function isManual(detector) {
    return detector?.type === 'manual' || detector?.type === 'manualAdvanceOnNext';
}
